import React, { useState, useEffect, useRef } from "react";
import {
  Checkbox,
  Row,
  Col,
  InputNumber,
  Form,
  Spin,
  message,
  Card,
  Space,
  Divider,
} from "antd";
import { useQuery } from "react-query";

import { Locale, ApiManager } from "../../utils";
import { useSelector } from "react-redux";

import "./productCommissionForm.scss";
import { getUserInfo } from "../../utils/redux/reducers/User";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();

function ProductCommissionForm({
  setCommissions,
  commissions,
  form,
  setSelectedLevels: setLevels,
}) {
  const [selectedLevels, setSelectedLevels] = useState([]);
  const user = useSelector((state) => getUserInfo(state));
  const token = user?.token;
  const didInitFromProduct = useRef(false);

  // Depend on token string only — getUserInfo() returns a new object every select,
  // so [user] in useCallback/useEffect would flood GET agent-level.
  const { data: agentLevelsData, isLoading } = useQuery(
    ["agent-levels", token],
    async () => {
      const res = await api.agent.list.run({ token });
      if (res.result) {
        return res.data || [];
      }
      throw new Error(res.message || "Failed to fetch agent levels");
    },
    {
      enabled: !!token,
      staleTime: 60 * 1000,
      onError: (err) => {
        message.error(err.message || "Failed to fetch agent levels");
      },
    }
  );

  const agentLevels = agentLevelsData || [];

  // Seed form once from product commissions. Do not re-run on parent re-renders
  // (unstable setLevels) or when unchecking levels — that would wipe edits.
  useEffect(() => {
    if (didInitFromProduct.current) return;
    if (!form || !commissions || commissions.length === 0) return;

    didInitFromProduct.current = true;
    const levelIds = commissions.map((comm) => {
      form.setFieldsValue({
        [`commissions[${comm.id}][commission]`]: comm.value.commission,
        [`commissions[${comm.id}][parent_commission]`]:
          comm.value.parent_commission,
      });
      return comm.id;
    });
    setSelectedLevels(levelIds);
    if (typeof setLevels === "function") {
      setLevels(levelIds);
    }
  }, [commissions, form, setLevels]);

  const handleLevelChange = (levelId, checked) => {
    if (checked) {
      const nextLevels = [...selectedLevels, levelId];
      setSelectedLevels(nextLevels);
      if (typeof setLevels === "function") {
        setLevels(nextLevels);
      }
      return;
    }

    const nextLevels = selectedLevels.filter((id) => id !== levelId);
    setSelectedLevels(nextLevels);
    if (typeof setLevels === "function") {
      setLevels(nextLevels);
    }
    if (typeof setCommissions === "function") {
      setCommissions(commissions.filter((comm) => comm.id !== levelId));
    }
    form?.setFieldsValue({
      [`commissions[${levelId}][commission]`]: undefined,
      [`commissions[${levelId}][parent_commission]`]: undefined,
    });
  };

  const getAgentLevelName = (agentLevelId) => {
    const level = agentLevels.find((item) => item.id === agentLevelId);
    return level ? i18n.getPropNameByLocale(level, "name") : "";
  };

  return (
    <div className="commission-form-area">
      <Spin spinning={isLoading}>
        <Card title={i18n.t("agentLevel.title")} size="big">
          <div style={{ marginBottom: 16, width: "100%" }}>
            <h4>{i18n.t("agentLevel.selectLevels")}</h4>
            <Row gutter={[16, 8]}>
              {agentLevels.map((level) => (
                <Col key={level.id} xs={24} sm={12} md={12} lg={8}>
                  <Checkbox
                    checked={selectedLevels.includes(level.id)}
                    onChange={(e) =>
                      handleLevelChange(level.id, e.target.checked)
                    }
                  >
                    {i18n.getPropNameByLocale(level, "name")}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </div>

          {selectedLevels.length > 0 && (
            <>
              <Divider />
              <div>
                <h4>{i18n.t("agentLevel.commissionSettings")}</h4>
                {selectedLevels.map((levelId) => (
                  <Row gutter={[16, 16]} key={levelId}>
                    <Card size="medium" title={getAgentLevelName(levelId)}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          name={`commissions[${levelId}][commission]`}
                          label={i18n.t("agentLevel.commission")}
                          rules={[
                            {
                              required: true,
                              message: i18n.t("agentLevel.commissionWarning"),
                            },
                            {
                              type: "number",
                              min: 0,
                              message: i18n.t(
                                "agentLevel.commissionRangeWarning"
                              ),
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder={i18n.t(
                              "agentLevel.commissionPlaceholder"
                            )}
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                            addonBefore="¥"
                          />
                        </Form.Item>

                        <Form.Item
                          name={`commissions[${levelId}][parent_commission]`}
                          label={i18n.t("agentLevel.parentCommission")}
                          rules={[
                            {
                              required: true,
                              message: i18n.t(
                                "agentLevel.parentCommissionWarning"
                              ),
                            },
                            {
                              type: "number",
                              min: 0,
                              message: i18n.t(
                                "agentLevel.commissionRangeWarning"
                              ),
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder={i18n.t(
                              "agentLevel.parentCommissionPlaceholder"
                            )}
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                            addonBefore="¥"
                          />
                        </Form.Item>
                      </Space>
                    </Card>
                  </Row>
                ))}
              </div>
            </>
          )}
        </Card>
      </Spin>
    </div>
  );
}

export default ProductCommissionForm;
